<!-- components/FbxModel.vue -->
<template>
  <div ref="threeContainer" class="three-container relative">
    <!-- 加载遮罩层 -->
    <div
        v-if="isLoading"
        class="loading-overlay absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 text-white z-10 transition-all duration-300"
    >
      <svg
          class="animate-spin w-10 h-10 mb-4 text-purple-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
      >
        <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
        ></circle>
        <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <p class="text-lg font-medium">{{ $t('common.modelLoading') }}</p>
      <p class="text-sm mt-1">{{ Math.round(loadingProgress * 100) }}%</p>
    </div>

    <!-- Three.js 渲染容器 -->
    <div ref="rendererContainer" class="w-full h-full"></div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { getModelUrl } from '@/utils/index';

export default {
  name: 'FbxModel',
  props: {
    modelPath: {
      type: String,
      required: true
    },
    cameraDistance: {
      type: Number,
      default: 5
    },
    enableControls: {
      type: Boolean,
      default: true
    },
    scale: {
      type: Object,
      default: () => ({ x: 1, y: 1, z: 1 })
    },
    position: {
      type: Object,
      default: () => ({ x: 0, y: 0, z: 0 })
    },
    rotation: {
      type: Object,
      default: () => ({ x: 0, y: 0, z: 0 })
    },
    background: {
      type: String,
      default: '#333333'
    },
    shouldLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      fbxModel: null,
      loader: null,
      isLoading: false,
      loadingProgress: 0
    };
  },
  async mounted() {
    if (!this.modelPath) {
      console.error('FbxModel: modelPath is required.');
      this.$emit('error', new Error('modelPath is required'));
      return;
    }

    if (this.shouldLoad) {
      this.startLoading();
    }
  },
  beforeDestroy() {
    this.cleanUp();
  },
  methods: {
    startLoading() {
      try {
        this.isLoading = true; // 开始加载
        this.initThree();
        this.loadFbxModel(this.modelPath);
        this.animate();
      } catch (error) {
        console.error('Failed to initialize FbxModel:', error);
        this.$emit('error', error);
        this.isLoading = false;
      }
    },
    initThree() {
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(this.background);

      const width = this.$refs.threeContainer.clientWidth;
      const height = this.$refs.threeContainer.clientHeight;

      this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
      this.camera.position.set(0, 0, this.cameraDistance);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);

      // 将渲染器 DOM 添加到专用容器
      this.$refs.rendererContainer.appendChild(this.renderer.domElement);

      // 光源
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      this.scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(5, 10, 7.5);
      this.scene.add(directionalLight);

      // 轨道控制器
      if (this.enableControls) {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
      }

      // 窗口大小变化
      window.addEventListener('resize', this.onWindowResize);
    },

    loadFbxModel(fbxFileUrl) {
      this.loader = new FBXLoader();

      // 处理 URL，解决 CORS 问题
      const processedUrl = getModelUrl(fbxFileUrl);

      this.loader.load(
          processedUrl,
          (object) => {
            console.log('FBX Model loaded:', object);
            this.fbxModel = object;

            object.scale.set(this.scale.x, this.scale.y, this.scale.z);
            object.position.set(this.position.x, this.position.y, this.position.z);
            object.rotation.set(this.rotation.x, this.rotation.y, this.rotation.z);

            this.scene.add(object);
            this.fitCameraToObject(object, this.cameraDistance);

            // 加载完成
            this.isLoading = false;
            this.loadingProgress = 1;
            this.$emit('loaded');
          },
          (xhr) => {
            this.loadingProgress = xhr.loaded / xhr.total;
            this.$emit('progress', this.loadingProgress);
          },
          (error) => {
            console.error('An error happened loading FBX:', error);
            this.$emit('error', error);
            this.isLoading = false;
          }
      );
    },

    fitCameraToObject(object, offset = 1.25) {
      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);

      if (maxDim === 0 || !isFinite(maxDim) || maxDim > 10000) {
        console.warn('Model size is invalid or excessively large:', maxDim);
        this.camera.position.set(0, 2, 10);
        this.camera.lookAt(0, 0, 0);
        this.camera.updateProjectionMatrix();
        return;
      }

      const fov = this.camera.fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
      cameraZ *= offset;

      const neededFar = cameraZ * 2;
      if (this.camera.far < neededFar) {
        this.camera.far = Math.max(neededFar, 1000);
        this.camera.updateProjectionMatrix();
      }

      this.camera.position.set(center.x, center.y, center.z + cameraZ);
      this.camera.lookAt(center);
      this.camera.updateProjectionMatrix();
    },

    animate() {
      requestAnimationFrame(this.animate);

      if (this.controls) {
        this.controls.update();
      }

      this.renderer.render(this.scene, this.camera);
    },

    onWindowResize() {
      const width = this.$refs.threeContainer.clientWidth;
      const height = this.$refs.threeContainer.clientHeight;

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    },

    cleanUp() {
      if (this.renderer) {
        this.renderer.dispose();
        this.renderer.forceContextLoss();
        if (this.$refs.rendererContainer && this.renderer.domElement.parentNode) {
          this.$refs.rendererContainer.removeChild(this.renderer.domElement);
        }
      }
      if (this.controls) {
        this.controls.dispose();
      }
      window.removeEventListener('resize', this.onWindowResize);

      if (this.fbxModel && this.scene) {
        this.scene.remove(this.fbxModel);
        this.disposeObject(this.fbxModel);
      }
    },

    disposeObject(object) {
      object.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => this.disposeMaterial(mat));
            } else {
              this.disposeMaterial(child.material);
            }
          }
        }
      });
    },

    disposeMaterial(material) {
      const maps = [
        'map', 'lightMap', 'aoMap', 'emissiveMap', 'bumpMap',
        'normalMap', 'displacementMap', 'roughnessMap',
        'metalnessMap', 'alphaMap'
      ];
      maps.forEach(map => {
        if (material[map]) material[map].dispose();
      });
      material.dispose();
    }
  },
  watch: {
    modelPath: {
      handler(newPath) {
        if (!newPath) return;

        // 清理旧模型
        if (this.fbxModel && this.scene) {
          this.scene.remove(this.fbxModel);
          this.disposeObject(this.fbxModel);
          this.fbxModel = null;
        }

        // 重新加载
        if (this.shouldLoad) {
          this.isLoading = true;
          this.loadFbxModel(newPath);
        }
      },
      immediate: false
    },
    shouldLoad: {
      handler(newVal) {
        if (newVal && this.modelPath && !this.fbxModel) {
          this.startLoading();
        }
      },
      immediate: false
    }
  }
};
</script>

<style scoped>
.three-container {
  width: 100%;
  height: 100%;
  background-color: #232A3E;
  position: relative;
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

/* 可选：如果使用 Tailwind CSS，上面的类已适配 */
/* 如果不使用 Tailwind，可用下面的纯 CSS 替代 */

/*
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-family: Arial, sans-serif;
}

.loading-overlay svg {
  width: 40px;
  height: 40px;
  margin-bottom: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
*/
</style>